import * as Api from '@ViewModels';
import { useState, useCallback, useEffect } from 'react';
import { convertRawRichTextContentStateToRichContentEditorState } from '../../../../models/UiUtils';
import { useBaseStepState } from './useBaseStepState';
import { useStepSave, type BaseStepOverrides } from './useStepSaveFactory';

interface UseHandwrittenCardStepProps {
	hwcStep: Api.HandwrittenCardAutomationStep | null;
	template: Api.IAutomationTemplate | null;
	stepId: string;
	readonly: boolean;
	onSaveSuccess?: () => void;
	onSaveError?: (error: Api.IOperationResultNoValue) => void;
}

interface UseHandwrittenCardStepSaveOverrides extends BaseStepOverrides {
	newName?: string;
	newTemplateId?: string;
	newContent?: Api.IRawRichTextContentState;
	newIncludeSignature?: boolean;
	newContactFilter?: Api.IContactsFilterRequest | null;
	newSendFrom?: Api.ISendFromOptions | null;
}

/**
 * Combined state + save hook for HandwrittenCardStepRenderer.
 *
 * Owns all local state (name, template selection, content, signature, fallback email)
 * plus the save/saveSenderOptions actions.
 */
export function useHandwrittenCardStep({
	hwcStep,
	template,
	stepId,
	readonly,
	onSaveSuccess,
	onSaveError,
}: UseHandwrittenCardStepProps) {
	// HWC-specific state
	const [name, setName] = useState(hwcStep?.name || '');
	const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
		hwcStep?.templateReference?.templateId ?? null
	);
	const [contentEditorState, setContentEditorState] = useState<Api.IRichContentEditorState>(
		convertRawRichTextContentStateToRichContentEditorState(hwcStep?.content)
	);
	const [includeSignature, setIncludeSignature] = useState(!hwcStep?.excludeSignature);
	const [hasFallbackEmail, setHasFallbackEmail] = useState(
		!!hwcStep?.conditionalSteps?.[Api.AutomationStepOutcome.Error]
	);

	// Base state (contact filter, validation, advanced options, auto-save)
	const baseState = useBaseStepState({
		step: hwcStep,
		template,
		stepId,
		readonly,
		onSaveSuccess,
		onSaveError,
		hasAdvancedOptions: step => {
			return !!(step?.contactFilter?.criteria || step?.sendFromOptions);
		},
	});

	useEffect(() => {
		setName(hwcStep?.name || '');
	}, [hwcStep?.name]);

	useEffect(() => {
		setSelectedTemplateId(hwcStep?.templateReference?.templateId ?? null);
	}, [hwcStep?.templateReference?.templateId]);

	useEffect(() => {
		setContentEditorState(convertRawRichTextContentStateToRichContentEditorState(hwcStep?.content));
	}, [hwcStep?.content]);

	useEffect(() => {
		setIncludeSignature(!hwcStep?.excludeSignature);
	}, [hwcStep?.excludeSignature]);

	useEffect(() => {
		setHasFallbackEmail(!!hwcStep?.conditionalSteps?.[Api.AutomationStepOutcome.Error]);
	}, [hwcStep?.conditionalSteps]);

	// Save logic
	const buildUpdatedStep = useCallback(
		(
			step: Api.HandwrittenCardAutomationStep | null,
			overrides: UseHandwrittenCardStepSaveOverrides
		): Partial<Api.HandwrittenCardAutomationStep> => {
			const effectiveName = overrides.newName ?? name;
			const effectiveContent = overrides.newContent ?? contentEditorState.getRawRichTextContent();
			const effectiveIncludeSignature = overrides.newIncludeSignature ?? includeSignature;
			const effectiveContactFilter =
				overrides.newContactFilter !== undefined ? overrides.newContactFilter : baseState.skipStepContactFilter;

			const updatedStep: Partial<Api.HandwrittenCardAutomationStep> = {
				...step,
				name: effectiveName,
				content: effectiveContent,
				excludeSignature: !effectiveIncludeSignature,
				contactFilter: effectiveContactFilter,
			};

			if (overrides.newTemplateId !== undefined) {
				updatedStep.templateReference = {
					templateId: overrides.newTemplateId,
					isCustomized: false,
					isSystemTemplate: false,
				};
			} else if (selectedTemplateId) {
				updatedStep.templateReference = {
					templateId: selectedTemplateId,
					isCustomized: step?.templateReference?.isCustomized || false,
					isSystemTemplate: step?.templateReference?.isSystemTemplate || false,
				};
			}

			if (overrides.newSendFrom !== undefined) {
				updatedStep.sendFromOptions = overrides.newSendFrom;
			}

			return updatedStep;
		},
		[name, selectedTemplateId, contentEditorState, includeSignature, baseState.skipStepContactFilter]
	);

	const { save } = useStepSave<Api.HandwrittenCardAutomationStep, UseHandwrittenCardStepSaveOverrides>({
		step: hwcStep,
		stepId,
		stepType: Api.AutomationStepType.HandwrittenCard,
		templateId: template?.id,
		autoSave: baseState.autoSave,
		buildUpdatedStep,
	});

	const saveSenderOptions = useCallback(
		(option: Api.ISendFromOptions | null) => {
			save({ newSendFrom: option });
		},
		[save]
	);

	return {
		// HWC-specific state
		name,
		setName,
		selectedTemplateId,
		setSelectedTemplateId,
		contentEditorState,
		setContentEditorState,
		includeSignature,
		setIncludeSignature,
		hasFallbackEmail,
		setHasFallbackEmail,
		// Base state
		showRequiredFieldError: baseState.showRequiredFieldError,
		setShowRequiredFieldError: baseState.setShowRequiredFieldError,
		advancedOptionsToggled: baseState.advancedOptionsToggled,
		setAdvancedOptionsToggled: baseState.setAdvancedOptionsToggled,
		skipStepContactFilter: baseState.skipStepContactFilter,
		setSkipStepContactFilter: baseState.setSkipStepContactFilter,
		autoSave: baseState.autoSave,
		clearInlineErrorMessage: baseState.clearInlineErrorMessage,
		// Save actions
		save,
		saveSenderOptions,
	};
}
